import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy,
  setDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Site Service - Manages organizational sites with geolocation
 * Supports the C3 Premium Mapping + Mass Action Module
 */

// Site types for classification (LEGACY - kept for backward compatibility)
// Note: Site types are now dynamic per organization, any text is accepted
export const SITE_TYPES = {
  CLINIC: 'clinic',
  OFFICE: 'office', 
  WAREHOUSE: 'warehouse',
  RETAIL: 'retail',
  MANUFACTURING: 'manufacturing',
  DENTAL: 'dental',
  HEALTH_CARE_CENTRE: 'health care centre',
  OPTICAL: 'optical',
  BMVS: 'bmvs',
  OTHER: 'other'
};

// Site status options - Crisis Management focused
export const SITE_STATUS = {
  ACTIVE: 'active',
  DEGRADED: 'degraded',
  EVACUATING: 'evacuating',
  EVACUATED: 'evacuated',
  CLOSED_AND_SECURE: 'closed_and_secure',
  UNKNOWN: 'unknown'
};

/**
 * Get all sites for an organization
 */
export const getSitesByOrganization = async (orgId) => {
  try {
    const sitesRef = collection(db, 'c3_sites');
    const q = query(
      sitesRef,
      where('organization_id', '==', orgId),
      orderBy('site_name', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      // Ensure all sites have a status, default to 'unknown'
      status: doc.data().status || SITE_STATUS.UNKNOWN
    }));
  } catch (error) {
    // Silently handle site fetching errors to reduce console noise
    // This is expected when Firebase permissions aren't configured
    throw error;
  }
};

/**
 * Get a single site by ID
 */
export const getSiteById = async (siteId) => {
  try {
    const siteRef = doc(db, 'c3_sites', siteId);
    const siteDoc = await getDoc(siteRef);
    
    if (!siteDoc.exists()) {
      throw new Error('Site not found');
    }
    
    return {
      id: siteDoc.id,
      ...siteDoc.data(),
      // Ensure site has a status, default to 'unknown'
      status: siteDoc.data().status || SITE_STATUS.UNKNOWN
    };
  } catch (error) {
    console.error('Error fetching site:', error);
    throw error;
  }
};

/**
 * Create a new site
 */
export const createSite = async (siteData, orgId) => {
  try {
    // Validate required fields
    validateSiteData(siteData);
    
    // Prepare site document
    const newSite = {
      ...siteData,
      organization_id: orgId,
      created_at: new Date(),
      updated_at: new Date(),
      status: siteData.status || SITE_STATUS.UNKNOWN
    };
    
    // Geocode if coordinates not provided
    if (!newSite.latitude || !newSite.longitude) {
      const geocodingResult = await geocodeAddress(newSite);
      if (geocodingResult) {
        newSite.latitude = geocodingResult.lat;
        newSite.longitude = geocodingResult.lng;
        
        // Auto-populate missing address fields from geocoding
        if (geocodingResult.addressComponents) {
          const components = geocodingResult.addressComponents;
          
          if (!newSite.suburb && components.suburb) {
            newSite.suburb = components.suburb;
          }
          if (!newSite.state && components.state) {
            newSite.state = components.state;
          }
          if (!newSite.postcode && components.postcode) {
            newSite.postcode = components.postcode;
          }
        }
      }
    }
    
    const sitesRef = collection(db, 'c3_sites');
    const docRef = await addDoc(sitesRef, newSite);
    
    return {
      id: docRef.id,
      ...newSite
    };
  } catch (error) {
    console.error('Error creating site:', error);
    throw error;
  }
};

/**
 * Update an existing site
 */
export const updateSite = async (siteId, siteData) => {
  try {
    validateSiteData(siteData);
    
    const updatedSite = {
      ...siteData,
      updated_at: new Date()
    };
    
    // Re-geocode if address changed and no coordinates provided
    if (!updatedSite.latitude || !updatedSite.longitude) {
      const geocodingResult = await geocodeAddress(updatedSite);
      if (geocodingResult) {
        updatedSite.latitude = geocodingResult.lat;
        updatedSite.longitude = geocodingResult.lng;
        
        // Auto-populate missing address fields from geocoding
        if (geocodingResult.addressComponents) {
          const components = geocodingResult.addressComponents;
          
          if (!updatedSite.suburb && components.suburb) {
            updatedSite.suburb = components.suburb;
          }
          if (!updatedSite.state && components.state) {
            updatedSite.state = components.state;
          }
          if (!updatedSite.postcode && components.postcode) {
            updatedSite.postcode = components.postcode;
          }
        }
      }
    }
    
    const siteRef = doc(db, 'c3_sites', siteId);
    await updateDoc(siteRef, updatedSite);
    
    return {
      id: siteId,
      ...updatedSite
    };
  } catch (error) {
    console.error('Error updating site:', error);
    throw error;
  }
};

/**
 * Update site status only
 */
export const updateSiteStatus = async (siteId, newStatus, userId) => {
  try {
    // Validate status
    const validStatuses = Object.values(SITE_STATUS);
    if (!validStatuses.includes(newStatus)) {
      throw new Error(`Invalid status: ${newStatus}`);
    }
    
    const siteRef = doc(db, 'c3_sites', siteId);
    await updateDoc(siteRef, {
      status: newStatus,
      updated_at: new Date(),
      last_status_updated_by: userId,
      last_status_updated_at: new Date()
    });
    
    return {
      id: siteId,
      status: newStatus,
      updated_at: new Date()
    };
  } catch (error) {
    console.error('Error updating site status:', error);
    throw error;
  }
};

/**
 * Delete a site
 */
export const deleteSite = async (siteId) => {
  try {
    const siteRef = doc(db, 'c3_sites', siteId);
    await deleteDoc(siteRef);
    return true;
  } catch (error) {
    console.error('Error deleting site:', error);
    throw error;
  }
};

/**
 * ORGANIZATION SITE TYPES MANAGEMENT
 * Handles dynamic site type registry per organization
 */

/**
 * Get all site types for an organization
 */
export const getOrgSiteTypes = async (orgId) => {
  try {
    const siteTypesRef = collection(db, 'c3_organisations', orgId, 'site_types');
    const snapshot = await getDocs(siteTypesRef);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching org site types:', error);
    return [];
  }
};

/**
 * Register a new site type for an organization
 */
export const registerSiteType = async (orgId, siteTypeData) => {
  try {
    const siteTypeId = siteTypeData.name.replace(/\s+/g, '_').toLowerCase();
    const siteTypeRef = doc(db, 'c3_organisations', orgId, 'site_types', siteTypeId);
    
    const siteTypeDoc = {
      name: siteTypeData.name,
      display_name: siteTypeData.display_name || toTitleCase(siteTypeData.name),
      color: siteTypeData.color,
      letter: siteTypeData.letter || siteTypeData.display_name.charAt(0).toUpperCase(),
      usage_count: 1,
      first_seen: new Date(),
      last_used: new Date(),
      created_by: siteTypeData.created_by || 'system',
      updated_at: new Date()
    };
    
    await setDoc(siteTypeRef, siteTypeDoc);
    
    return {
      id: siteTypeId,
      ...siteTypeDoc
    };
  } catch (error) {
    console.error('Error registering site type:', error);
    throw error;
  }
};

/**
 * Update usage count for a site type
 */
export const updateSiteTypeUsage = async (orgId, siteTypeName) => {
  try {
    const siteTypeId = siteTypeName.replace(/\s+/g, '_').toLowerCase();
    const siteTypeRef = doc(db, 'c3_organisations', orgId, 'site_types', siteTypeId);
    const siteTypeDoc = await getDoc(siteTypeRef);
    
    if (siteTypeDoc.exists()) {
      await updateDoc(siteTypeRef, {
        usage_count: (siteTypeDoc.data().usage_count || 0) + 1,
        last_used: new Date(),
        updated_at: new Date()
      });
    }
  } catch (error) {
    console.error('Error updating site type usage:', error);
  }
};

/**
 * Calculate color distance between two hex colors
 */
const colorDistance = (color1, color2) => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');
  
  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);
  
  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);
  
  return Math.sqrt(Math.pow(r2 - r1, 2) + Math.pow(g2 - g1, 2) + Math.pow(b2 - b1, 2));
};

/**
 * Check if color is too similar to existing colors
 */
const colorTooSimilar = (hue, saturation, lightness, existingColors) => {
  const newColor = hslToHex(hue, saturation, lightness);
  
  for (const existingColor of existingColors) {
    if (colorDistance(newColor, existingColor) < 40) {
      return true;
    }
  }
  
  return false;
};

/**
 * Convert HSL to HEX color
 */
const hslToHex = (h, s, l) => {
  h = h / 360;
  s = s / 100;
  l = l / 100;
  
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  
  const r = hue2rgb(p, q, h + 1/3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1/3);
  
  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Assign color to site type using golden ratio distribution
 */
const assignSiteTypeColor = (index, existingColors = []) => {
  // Golden ratio for even distribution around color wheel
  const goldenAngle = 137.508;
  const saturation = 65;
  const lightness = 50;
  
  let hue = (index * goldenAngle) % 360;
  
  // Avoid conflicts with existing colors
  while (colorTooSimilar(hue, saturation, lightness, existingColors)) {
    hue = (hue + 30) % 360;
  }
  
  return hslToHex(hue, saturation, lightness);
};

/**
 * Convert text to title case
 */
const toTitleCase = (text) => {
  return text.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Auto-discover and register site types from bulk upload data
 */
export const discoverAndRegisterSiteTypes = async (sites, orgId) => {
  try {
    // Get unique site types from upload data
    const uniqueTypes = [...new Set(sites.map(s => s.site_type?.toLowerCase()).filter(Boolean))];
    
    // Get existing site types for this organization
    const existingTypes = await getOrgSiteTypes(orgId);
    const existingTypeNames = existingTypes.map(t => t.name.toLowerCase());
    
    // Find new types that need registration
    const newTypes = uniqueTypes.filter(type => !existingTypeNames.includes(type));
    
    console.log(`🎨 Discovered ${newTypes.length} new site types:`, newTypes);
    
    // Register new site types with auto-assigned colors
    for (const [index, typeName] of newTypes.entries()) {
      const color = assignSiteTypeColor(existingTypes.length + index, existingTypes.map(t => t.color));
      
      await registerSiteType(orgId, {
        name: typeName,
        display_name: toTitleCase(typeName),
        color: color,
        created_by: 'bulk_upload'
      });
      
      console.log(`✅ Registered new site type: ${typeName} -> ${color}`);
    }
    
    return newTypes.length;
  } catch (error) {
    console.error('Error discovering site types:', error);
    throw error;
  }
};

/**
 * Assign color to new site type using golden ratio distribution
 */


/**
 * Bulk upload sites from CSV/Excel data
 */
export const bulkUploadSites = async (sitesData, orgId) => {
  try {
    const results = {
      processed: 0,
      successful: 0,
      errors: []
    };
    
    // Auto-discover and register new site types
    console.log('🎨 Auto-discovering site types from bulk upload...');
    const newTypesCount = await discoverAndRegisterSiteTypes(sitesData, orgId);
    if (newTypesCount > 0) {
      console.log(`✅ Registered ${newTypesCount} new site types for organization`);
    }
    
    for (let i = 0; i < sitesData.length; i++) {
      const siteData = sitesData[i];
      results.processed++;
      
      try {
        // Validate row data
        validateSiteData(siteData, i + 1);
        
        // Create site
        await createSite(siteData, orgId);
        results.successful++;
        
      } catch (error) {
        results.errors.push({
          row: i + 1,
          data: siteData,
          error: error.message
        });
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error in bulk upload:', error);
    throw error;
  }
};

/**
 * Get sites within a geographic boundary (for draw-to-select)
 */
export const getSitesInBounds = async (orgId, bounds) => {
  try {
    const sites = await getSitesByOrganization(orgId);
    
    return sites.filter(site => {
      if (!site.latitude || !site.longitude) return false;
      
      return (
        site.latitude >= bounds.south &&
        site.latitude <= bounds.north &&
        site.longitude >= bounds.west &&
        site.longitude <= bounds.east
      );
    });
  } catch (error) {
    console.error('Error filtering sites by bounds:', error);
    throw error;
  }
};

/**
 * Validate site data structure - Excel-friendly with auto-correction
 */
const validateSiteData = (siteData, rowNumber = null) => {
  const prefix = rowNumber ? `Row ${rowNumber}: ` : '';
  
  // Helper function to convert any value to trimmed string
  const toTrimmedString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };
  
  // Helper function to check if value is empty
  const isEmpty = (value) => {
    const str = toTrimmedString(value);
    return str === '' || str === 'null' || str === 'undefined';
  };
  
  // Validate and clean site name
  siteData.site_name = toTrimmedString(siteData.site_name);
  if (isEmpty(siteData.site_name)) {
    throw new Error(`${prefix}Site name is required`);
  }
  
  // Validate and clean site type (accept any non-empty text)
  siteData.site_type = toTrimmedString(siteData.site_type).toLowerCase();
  if (isEmpty(siteData.site_type)) {
    throw new Error(`${prefix}Site type is required`);
  }
  
  // Validate and clean address
  siteData.address_line_1 = toTrimmedString(siteData.address_line_1);
  if (isEmpty(siteData.address_line_1)) {
    throw new Error(`${prefix}Address line 1 is required`);
  }
  
  // Validate and clean suburb (optional)
  if (siteData.suburb && !isEmpty(siteData.suburb)) {
  siteData.suburb = toTrimmedString(siteData.suburb);
  }
  
  // Validate and clean state (optional)
  if (siteData.state && !isEmpty(siteData.state)) {
    siteData.state = toTrimmedString(siteData.state);
  } else {
    siteData.state = ''; // Set empty if not provided
  }
  
  // Validate and clean postcode (optional - convert numbers to strings)
  if (siteData.postcode && !isEmpty(siteData.postcode)) {
    siteData.postcode = toTrimmedString(siteData.postcode);
  }
  
  // Validate and clean country
  siteData.country = toTrimmedString(siteData.country);
  if (isEmpty(siteData.country)) {
    throw new Error(`${prefix}Country is required`);
  }
  
  // Validate and clean manager name (optional)
  if (siteData.site_manager_name && !isEmpty(siteData.site_manager_name)) {
    siteData.site_manager_name = toTrimmedString(siteData.site_manager_name);
  }
  
  // Validate and clean mobile number (optional)
  if (siteData.site_manager_mobile && !isEmpty(siteData.site_manager_mobile)) {
    siteData.site_manager_mobile = toTrimmedString(siteData.site_manager_mobile);
    
    // More lenient mobile number validation
    const mobileRegex = /^[\+]?[\d\s\-\(\)\.\/]{8,}$/;
  if (!mobileRegex.test(siteData.site_manager_mobile)) {
      throw new Error(`${prefix}Site manager mobile number format is invalid. Expected format: +61400123456 or 0400123456. Got: "${siteData.site_manager_mobile}"`);
    }
  }
  
  // Clean optional fields
  if (siteData.address_line_2) {
    siteData.address_line_2 = toTrimmedString(siteData.address_line_2);
  }
  
  if (siteData.region) {
    siteData.region = toTrimmedString(siteData.region);
  }
  
  // Validate email if provided (and not empty)
  if (siteData.site_manager_email && !isEmpty(siteData.site_manager_email)) {
    siteData.site_manager_email = toTrimmedString(siteData.site_manager_email);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(siteData.site_manager_email)) {
      throw new Error(`${prefix}Site manager email format is invalid: "${siteData.site_manager_email}"`);
    }
  }
  
  // Validate coordinates if provided (handle strings and numbers)
  if (siteData.latitude !== undefined && siteData.latitude !== null && !isEmpty(siteData.latitude)) {
    const lat = parseFloat(siteData.latitude);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      throw new Error(`${prefix}Latitude must be a number between -90 and 90. Got: "${siteData.latitude}"`);
    }
    siteData.latitude = lat;
  }
  
  if (siteData.longitude !== undefined && siteData.longitude !== null && !isEmpty(siteData.longitude)) {
    const lng = parseFloat(siteData.longitude);
    if (isNaN(lng) || lng < -180 || lng > 180) {
      throw new Error(`${prefix}Longitude must be a number between -180 and 180. Got: "${siteData.longitude}"`);
    }
    siteData.longitude = lng;
  }
  
  // Validate status if provided (case insensitive), default to 'unknown'
  if (siteData.status && !isEmpty(siteData.status)) {
    siteData.status = toTrimmedString(siteData.status).toLowerCase();
    if (!Object.values(SITE_STATUS).includes(siteData.status)) {
      throw new Error(`${prefix}Invalid status. Must be one of: ${Object.values(SITE_STATUS).join(', ')}. Got: "${siteData.status}"`);
    }
  } else {
    // Default to 'unknown' status if not provided
    siteData.status = SITE_STATUS.UNKNOWN;
  }
};

/**
 * Extract address components from Google Maps geocoding response
 * Maps international address formats for AU/NZ/HK
 */
const extractAddressComponents = (addressComponents, country) => {
  const components = {};
  
  // Define mapping for international address formats
  const componentMappings = {
    'locality': 'suburb',
    'sublocality': 'suburb',
    'sublocality_level_1': 'suburb',
    'administrative_area_level_1': 'state',
    'postal_code': 'postcode'
  };
  
  // Country-specific handling for AU/NZ/HK
  const countryLower = (country || '').toLowerCase();
  const hasStates = ['australia', 'au'].includes(countryLower);
  
  for (const component of addressComponents || []) {
    for (const type of component.types) {
      if (componentMappings[type]) {
        const mappedField = componentMappings[type];
        
        // Skip state for countries without states (NZ, HK)
        if (mappedField === 'state' && !hasStates) {
          continue;
        }
        
        // Use long_name for better readability
        components[mappedField] = component.long_name;
        break; // Take first matching type
      }
    }
  }
  
  return components;
};

/**
 * Geocode an address using Google Maps Geocoding API
 * Uses REACT_APP_GOOGLE_MAPS_API_KEY from environment
 */
const geocodeAddress = async (siteData) => {
  try {
    // Construct full address
    const addressParts = [
      siteData.address_line_1,
      siteData.address_line_2,
      siteData.suburb,
      siteData.state,
      siteData.postcode,
      siteData.country
    ].filter(Boolean);
    
    const fullAddress = addressParts.join(', ');
    
    console.log('🗺️ Geocoding address:', fullAddress);
    
    // Get API key from environment
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.log('⚠️ Google Maps API key not configured in environment');
      return null;
    }
    
    // Call Google Maps Geocoding API
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${apiKey}`,
      { 
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.status === 'OK' && data.results.length > 0) {
      const result = data.results[0];
      const location = result.geometry.location;
      const addressComponents = extractAddressComponents(result.address_components, siteData.country);
      
      const geocodingResult = {
        lat: location.lat,
        lng: location.lng,
        addressComponents: addressComponents
      };
      
      console.log(`✅ Geocoding success: ${fullAddress} -> ${geocodingResult.lat}, ${geocodingResult.lng}`);
      return geocodingResult;
    } else {
      console.log(`⚠️ Geocoding failed for "${fullAddress}": ${data.status}`);
      if (data.error_message) {
        console.log(`   Error: ${data.error_message}`);
      }
      return null;
    }
    
  } catch (error) {
    console.error('❌ Geocoding error for', siteData.address_line_1, ':', error.message);
    return null;
  }
};

/**
 * Parse a single CSV line respecting quoted fields
 */
const parseCSVLine = (line) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Double quote escape
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      // End of field
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Add final field
  result.push(current.trim());
  
  return result;
};

/**
 * Parse CSV data for bulk upload
 */
export const parseCSVData = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV must contain at least a header row and one data row');
  }
  
  const rawHeaders = parseCSVLine(lines[0]);
  
  // Extract field names from descriptive headers (e.g., "state (REQUIRED - for bulk SMS filtering)" -> "state")
  const headers = rawHeaders.map(header => {
    const trimmed = header.trim();
    // If header contains parentheses, extract field name before the first parenthesis
    const parenIndex = trimmed.indexOf('(');
    if (parenIndex !== -1) {
      return trimmed.substring(0, parenIndex).trim();
    }
    return trimmed;
  });
  const sites = [];
  
  // Required column mappings
  const columnMappings = {
    'site_name': ['site_name', 'site name', 'name'],
    'site_type': ['site_type', 'site type', 'type'],
    'address_line_1': ['address_line_1', 'address line 1', 'address1', 'address'],
    'country': ['country']
  };
  
  // Optional column mappings
  const optionalMappings = {
    'address_line_2': ['address_line_2', 'address line 2', 'address2'],
    'suburb': ['suburb', 'city', 'locality'],
    'state': ['state', 'province', 'region'],
    'postcode': ['postcode', 'postal_code', 'zip', 'zip_code'],
    'region': ['region'],
    'latitude': ['latitude', 'lat'],
    'longitude': ['longitude', 'lng', 'lon'],
    'status': ['status'],
    'site_manager_name': ['site_manager_name', 'manager_name', 'manager name', 'contact_name'],
    'site_manager_mobile': ['site_manager_mobile', 'manager_mobile', 'mobile', 'phone'],
    'site_manager_email': ['site_manager_email', 'manager_email', 'email']
  };
  
  // Find column indices
  const getColumnIndex = (fieldMappings) => {
    for (const variant of fieldMappings) {
      const index = headers.findIndex(h => h.toLowerCase() === variant.toLowerCase());
      if (index !== -1) return index;
    }
    return -1;
  };
  
  const columnIndices = {};
  
  // Check required columns
  for (const [field, mappings] of Object.entries(columnMappings)) {
    const index = getColumnIndex(mappings);
    if (index === -1) {
      console.log('❌ Available headers:', headers);
      throw new Error(`Required column not found: ${field} (looking for: ${mappings.join(', ')})`);
    }
    columnIndices[field] = index;
  }
  
  // Get optional columns
  for (const [field, mappings] of Object.entries(optionalMappings)) {
    const index = getColumnIndex(mappings);
    if (index !== -1) {
      columnIndices[field] = index;
    }
  }
  
  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCSVLine(lines[i]);
    
    // Skip empty rows
    if (cells.every(cell => !cell || cell.trim() === '')) {
      continue;
    }
    
    const site = {};
    
    // Extract all fields (required and optional)
    for (const [field, index] of Object.entries(columnIndices)) {
      if (index < cells.length) {
        const cellValue = cells[index];
        // Only add non-empty values
        if (cellValue && cellValue.trim() !== '') {
          site[field] = cellValue.trim();
        }
      }
    }
    
    // Helper function to check if field has meaningful value
    const hasValue = (field) => field && typeof field === 'string' && field.trim() !== '';
    
    // Skip rows that don't have minimum required data (location only)
    if (!hasValue(site.site_name) || !hasValue(site.site_type) || !hasValue(site.address_line_1) || !hasValue(site.country)) {
      console.log(`⚠️ Skipping row ${i + 1}: Missing essential location data`, site);
      console.log(`🔍 Debug: site_name="${site.site_name}" (${typeof site.site_name}), site_type="${site.site_type}" (${typeof site.site_type}), address_line_1="${site.address_line_1}" (${typeof site.address_line_1}), country="${site.country}" (${typeof site.country})`);
      continue;
    }
    
    sites.push(site);
  }
  
  return sites;
}; 

/**
 * DELETE ALL SITES FOR AN ORGANIZATION (SUPER ADMIN ONLY)
 * Deletes all sites for an organization in batches for safety
 */
export const deleteAllSitesByOrganization = async (orgId, adminUserId) => {
  try {
    console.log(`🗑️ Starting deletion of all sites for organization: ${orgId}`);
    
    // Get all sites for this organization
    const sites = await getSitesByOrganization(orgId);
    
    if (sites.length === 0) {
      console.log('No sites found to delete');
      return { deleted: 0, errors: [] };
    }
    
    console.log(`Found ${sites.length} sites to delete:`, sites.map(s => `${s.site_name} (${s.id})`));
    
    const results = {
      deleted: 0,
      errors: []
    };
    
    // Delete in batches of 500 for Firebase safety
    const batchSize = 500;
    const batches = [];
    
    for (let i = 0; i < sites.length; i += batchSize) {
      batches.push(sites.slice(i, i + batchSize));
    }
    
    // Process each batch
    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      console.log(`Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} sites)`);
      
      // Delete all sites in this batch
      const deletePromises = batch.map(async (site) => {
        try {
          console.log(`🗑️ Deleting site: ${site.site_name} (${site.id})`);
          await deleteSite(site.id);
          results.deleted++;
          console.log(`✅ Successfully deleted: ${site.site_name} (${site.id})`);
          return { success: true, siteId: site.id };
        } catch (error) {
          console.error(`❌ Failed to delete site: ${site.site_name} (${site.id})`, error);
          results.errors.push({
            siteId: site.id,
            siteName: site.site_name,
            error: error.message
          });
          return { success: false, siteId: site.id, error: error.message };
        }
      });
      
      const batchResults = await Promise.all(deletePromises);
      const batchSuccessful = batchResults.filter(r => r.success).length;
      const batchFailed = batchResults.filter(r => !r.success).length;
      
      console.log(`Batch ${batchIndex + 1} complete: ${batchSuccessful} successful, ${batchFailed} failed`);
      
      // Add a small delay between batches to avoid overwhelming Firebase
      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    console.log(`✅ Site deletion complete. Deleted: ${results.deleted}, Errors: ${results.errors.length}`);
    
    if (results.errors.length > 0) {
      console.warn('❌ Deletion errors:', results.errors);
    }
    
    return results;
    
  } catch (error) {
    console.error('Error deleting all sites:', error);
    throw error;
  }
};

/**
 * EXPORT SITES TO CSV
 * Exports all sites for an organization to CSV format
 */
export const exportSitesToCSV = (sites, orgName) => {
  try {
    // CSV Headers - matches the upload template structure
    const headers = [
      'site_name',
      'site_type', 
      'address_line_1',
      'address_line_2',
      'suburb',
      'state',
      'postcode',
      'country',
      'region',
      'latitude',
      'longitude',
      'status',
      'site_manager_name',
      'site_manager_mobile',
      'site_manager_email'
    ];
    
    // Convert sites to CSV rows
    const csvRows = sites.map(site => {
      return [
        site.site_name || '',
        site.site_type || '',
        site.address_line_1 || '',
        site.address_line_2 || '',
        site.suburb || '',
        site.state || '',
        site.postcode || '',
        site.country || '',
        site.region || '',
        site.latitude || '',
        site.longitude || '',
        site.status || 'active',
        site.site_manager_name || '',
        site.site_manager_mobile || '',
        site.site_manager_email || ''
      ].map(field => {
        // Escape quotes and wrap in quotes if contains comma
        const fieldStr = String(field);
        if (fieldStr.includes(',') || fieldStr.includes('"') || fieldStr.includes('\n')) {
          return `"${fieldStr.replace(/"/g, '""')}"`;
        }
        return fieldStr;
      }).join(',');
    });
    
    // Combine headers and data
    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    // Generate filename
    const sanitizedOrgName = orgName.replace(/[^a-zA-Z0-9]/g, '_');
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `sites_export_${sanitizedOrgName}_${dateStr}.csv`;
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    
    console.log(`✅ Exported ${sites.length} sites to ${filename}`);
    return filename;
    
  } catch (error) {
    console.error('Error exporting sites:', error);
    throw error;
  }
};

/**
 * AUDIT LOG SITE OPERATIONS
 * Logs site management operations for compliance
 */
export const auditLogSiteOperation = async (orgId, operation, details, adminUserId) => {
  try {
    // Import activityLogger dynamically to avoid circular imports
    const { logActivity } = await import('../utils/activityLogger');
    
    const auditData = {
      type: 'SITE_MANAGEMENT',
      subType: operation, // 'BULK_DELETE', 'BULK_UPLOAD', 'EXPORT'
      details: {
        organizationId: orgId,
        adminUserId: adminUserId,
        timestamp: new Date().toISOString(),
        ...details
      },
      priority: 'HIGH' // Site operations are high priority
    };
    
    await logActivity({
      type: auditData.type,
      userId: adminUserId,
      userEmail: null, // Will be resolved by the function
      organisationId: orgId,
      details: auditData.details,
      metadata: auditData.details
    });
    
    console.log(`📝 Audit logged: ${operation} for org ${orgId}`);
    
  } catch (error) {
    console.error('Error logging site operation:', error);
    // Don't throw - audit logging failure shouldn't break the main operation
  }
};

/**
 * REPLACE ALL SITES (COMPLETE WORKFLOW)
 * Comprehensive function that handles the full replace workflow
 */
export const replaceAllSites = async (orgId, orgName, newSitesData, adminUserId) => {
  try {
    console.log(`🔄 Starting complete site replacement for organization: ${orgId}`);
    
    const operationId = Date.now().toString();
    const results = {
      operationId,
      success: false,
      deleted: 0,
      uploaded: 0,
      errors: []
    };
    
    // Step 1: Audit log the start of operation
    await auditLogSiteOperation(orgId, 'BULK_REPLACE_START', {
      operationId,
      newSitesCount: newSitesData.length,
      adminUserId
    }, adminUserId);
    
    // Step 2: Get current sites count for verification
    const currentSites = await getSitesByOrganization(orgId);
    console.log(`📊 Found ${currentSites.length} existing sites to delete`);
    
    // Step 3: Delete all existing sites
    console.log('🗑️ Deleting all existing sites...');
    const deleteResults = await deleteAllSitesByOrganization(orgId, adminUserId);
    results.deleted = deleteResults.deleted;
    results.errors.push(...deleteResults.errors);
    
    console.log(`🗑️ Deletion complete. Deleted: ${deleteResults.deleted}, Errors: ${deleteResults.errors.length}`);
    
    // Step 4: Verify deletion completed
    const remainingSites = await getSitesByOrganization(orgId);
    console.log(`📊 Remaining sites after deletion: ${remainingSites.length}`);
    
    if (remainingSites.length > 0) {
      console.warn(`⚠️ Warning: ${remainingSites.length} sites still exist after deletion`);
      results.errors.push({
        type: 'DELETION_INCOMPLETE',
        message: `${remainingSites.length} sites could not be deleted`,
        remainingSites: remainingSites.length
      });
    }
    
    // Step 5: Upload new sites
    console.log('📤 Uploading new sites...');
    const uploadResults = await bulkUploadSites(newSitesData, orgId);
    results.uploaded = uploadResults.successful;
    results.errors.push(...uploadResults.errors);
    
    console.log(`📤 Upload complete. Uploaded: ${uploadResults.successful}, Errors: ${uploadResults.errors.length}`);
    
    // Step 6: Final verification
    const finalSites = await getSitesByOrganization(orgId);
    console.log(`📊 Final site count: ${finalSites.length}`);
    
    // Step 7: Determine success
    results.success = results.errors.length === 0 && remainingSites.length === 0;
    
    // Step 8: Audit log the completion
    await auditLogSiteOperation(orgId, 'BULK_REPLACE_COMPLETE', {
      operationId,
      initialSites: currentSites.length,
      deleted: results.deleted,
      uploaded: results.uploaded,
      finalSites: finalSites.length,
      errors: results.errors.length,
      success: results.success,
      adminUserId
    }, adminUserId);
    
    console.log(`✅ Site replacement complete:`, results);
    return results;
    
  } catch (error) {
    console.error('Error in site replacement:', error);
    
    // Audit log the failure
    await auditLogSiteOperation(orgId, 'BULK_REPLACE_FAILED', {
      error: error.message,
      adminUserId
    }, adminUserId);
    
    throw error;
  }
}; 